import { Component, OnDestroy, OnInit, ViewChild, Inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { Subject, Subscription } from 'rxjs';
import { find } from 'lodash';

import {
  EntityCacheProvider,
  NotificationService,
  DevicesService,
  DialogService,
  CommonRegEx
} from '@nrwl/common-lib';
import { DataSourceService } from '../../../service/datatsource.service';

@Component({
  selector: 'edit-managed-device',
  templateUrl: './edit-managed-device.component.html',
  styleUrls: ['./edit-managed-device.component.scss']
})
export class EditManagedDeviceComponent implements OnInit, OnDestroy {
  @ViewChild('deviceForm', { static: true }) form: NgForm;
  managedDeviceObj = {
    name: '',
    vendor: '',
    model: '',
    version: '',
    location: '',
    latitude: '',
    longitude: ''
  };
  esamAttributes = {
    isPMRetrievalChecked: false,
    enableJmepRelease: false,
    isControllerSwitchoverChecked: false,
    jmepStatusFrequency: '',
    pmInterval: '',
    jmepReleaseDelay: '',
    switchoverDelay: '',
    jmepReleaseDelayInMinutes: null,
    isEsamAttributesChanged: false,
    isEsam: false,
    esamURL: '',
    esamNewConfigsSupportMinVersion: '12.02',
    isNewConfigSupported: false,
    jmepReleaseMinMinutes: 5,
    jmepReleaseMaxHours: 24
  };
  esamConfigData: object;
  //eslint-disable-next-line
  jmepReleaseDelayHours: any;
  //eslint-disable-next-line
  jmepReleaseDelayMinutes: any;
  //eslint-disable-next-line
  onCheckEventInfo: any;
  invalidTimeMsg = '';
  isLoading = false;
  editMode = false;
  // eslint-disable-next-line
  selectedDeviceObj: any;
  isGeoRedundancyEnabled = false;
  latitudeIPRegex: RegExp = CommonRegEx.LATITUDE;
  longitudeIPRegex: RegExp = CommonRegEx.LONGITUDE;
  private readonly unsubscribe: Subject<void> = new Subject();
  private cacheObj: Subscription;

  constructor(
    public dialogRef: MatDialogRef<EditManagedDeviceComponent>,
    readonly entityCache: EntityCacheProvider,
    private notificationService: NotificationService,
    private dataSourceService: DataSourceService,
    private readonly deviceService: DevicesService,
    private readonly dialogService: DialogService,
    @Inject(MAT_DIALOG_DATA) public data: any // eslint-disable-line
  ) {}

  ngOnInit() {
    this.getDeviceData();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    if (this.cacheObj) {
      this.cacheObj.unsubscribe();
    }
  }

  getDeviceData(): void {
    this.isLoading = true;
    this.deviceService
      .getDeviceById$(this.data?.device?.dataItem?.uniqueId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(res => {
        this.selectedDeviceObj = res[0];
        this.assignData(this.selectedDeviceObj);
        if (!this.esamAttributes.isEsam) {
          this.isLoading = false;
        }
        if (this.esamAttributes.isEsam) {
          this.dataSourceService
            .getEsamConfig(this.esamAttributes.esamURL)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(res => {
              this.assignEsamData(res?.data?.value?.data);
              this.isLoading = false;
            });
        }
      });
  }

  saveDevice(): void {
    const payload = this.createPayLoad();
    if (this.esamAttributes.isEsam) {
      this.esamAttributesChanged();
    }
    if (this.data?.editMode) {
      this.updateDevice(payload);
    }
  }
  onChecked(type: string, $event: Event): void {
    this.onCheckEventInfo = $event;
    if (type === 'pmRetrieval') {
      if (this.onCheckEventInfo.checked === true) {
        this.esamAttributes.isPMRetrievalChecked = true;
      } else {
        this.esamAttributes.isPMRetrievalChecked = false;
      }
    } else if (type === 'jmepReleaseDelay') {
      if (this.esamAttributes.enableJmepRelease) {
        this.jmepReleaseConfirmation($event);
      }
    } else if (type === 'controllerSwitchover') {
      if (this.onCheckEventInfo.checked === true) {
        this.esamAttributes.isControllerSwitchoverChecked = true;
      } else {
        this.esamAttributes.isControllerSwitchoverChecked = false;
      }
    }
  }
  //eslint-disable-next-line
  callBackJMEPReleaseFunction(service): void {
    service.callBackJMEPReleaseDelay();
  }
  callBackJMEPReleaseDelay(): void {
    this.esamAttributes.enableJmepRelease = true;
  }
  cancelJMEPRelease(): void {
    this.esamAttributes.enableJmepRelease = false;
  }
  //eslint-disable-next-line
  cancelJMEPReleaseFunction(service): void {
    service.cancelJMEPRelease();
  }
  // eslint-disable-next-line
  jmepReleaseConfirmation(event): void {
    const params = {
      id: 'datasource.enableJmepRelease',
      callBackFnParams: event,
      callBackService: this,
      callBackFuntion: this.callBackJMEPReleaseFunction,
      cancelCallback: this.cancelJMEPReleaseFunction
    };
    this.dialogService.showDialog('confirm', 'body', params);
  }

  // eslint-disable-next-line
  createPayLoad(): Array<any> {
    const payload = [];
    const formData = this.form.form.value;
    this.selectedDeviceObj.name = formData['managedDeviceObj.name'];
    const locObj = {
      address: formData['managedDeviceObj.location'],
      position: {
        lat: formData['managedDeviceObj.latitude'] ? formData['managedDeviceObj.latitude'] : 0,
        lon: formData['managedDeviceObj.latitude'] ? formData['managedDeviceObj.longitude'] : 0
      }
    };
    this.selectedDeviceObj.extension.location = locObj;
    payload.push(this.selectedDeviceObj);
    return payload;
  }
  // eslint-disable-next-line
  createEsamPayload(): any {
    const esamPayload = {
      format: 'json',
      id: 'esam-config',
      value: {
        data: []
      }
    };

    const updatedEsamFieldsOptions = [];
    updatedEsamFieldsOptions.push({
      name: 'jmep-status-poll-frequency-in-seconds',
      value: this.esamAttributes.jmepStatusFrequency
    });
    updatedEsamFieldsOptions.push({
      name: 'esam-traffic-pm-period-in-minutes',
      value: this.esamAttributes.pmInterval
    });
    updatedEsamFieldsOptions.push({
      name: 'esam-traffic-pm-retrieval-enabled',
      value: this.esamAttributes.isPMRetrievalChecked
    });
    if (this.esamAttributes.isNewConfigSupported) {
      updatedEsamFieldsOptions.push({
        name: 'enable-controller-switchover',
        value: this.esamAttributes.isControllerSwitchoverChecked
      });
      updatedEsamFieldsOptions.push({
        name: 'switchover-delay',
        value: this.esamAttributes.switchoverDelay
      });
      updatedEsamFieldsOptions.push({
        name: 'enable-jmep-release',
        value: this.esamAttributes.enableJmepRelease
      });
      if (this.esamAttributes.enableJmepRelease) {
        updatedEsamFieldsOptions.push({
          name: 'jmep-release-delay',
          value: this.esamAttributes.jmepReleaseDelayInMinutes
        });
      }
    }
    for (let x = 0; x < updatedEsamFieldsOptions.length; x++) {
      esamPayload.value.data.push(updatedEsamFieldsOptions[x]);
    }
    return esamPayload;
  }
  //eslint-disable-next-line
  assignData(data: any): void {
    if (data?.model?.model === 'ESAM') {
      this.esamAttributes.esamURL = data.connections[0].uri;
      this.esamAttributes.isEsam = true;
      const splitVersion = data?.model?.version.split(' ');
      this.esamAttributes.isNewConfigSupported = this.isNewConfigsSupported(splitVersion[0]);
    }

    this.managedDeviceObj.name = data.name;
    this.managedDeviceObj.vendor = data?.model?.vendor;
    this.managedDeviceObj.model = data?.model?.model;
    this.managedDeviceObj.version = data?.model?.version;
    this.managedDeviceObj.location = data.extension.location?.address;
    this.managedDeviceObj.latitude = data.extension.location?.position?.lat;
    this.managedDeviceObj.longitude = data.extension.location?.position?.lon;
  }
  //eslint-disable-next-line
  assignEsamData(data: any): void {
    this.esamConfigData = data;
    this.esamAttributes.jmepStatusFrequency = data[0].value;
    this.esamAttributes.isControllerSwitchoverChecked = JSON.parse(data[3].value);
    this.esamAttributes.switchoverDelay = data[4].value;
    this.esamAttributes.enableJmepRelease = JSON.parse(data[5].value);
    this.esamAttributes.jmepReleaseDelay = this.toHoursAndMinutes(data[6].value);
    this.esamAttributes.isPMRetrievalChecked = JSON.parse(data[1].value);
    this.esamAttributes.pmInterval = JSON.parse(data[2].value);
    this.onChangeJmepReleaseDelay(this.esamAttributes.jmepReleaseDelay);
  }
  //eslint-disable-next-line
  esamAttributesChanged(): any {
    if (
      this.esamConfigData[0].value === this.esamAttributes.jmepStatusFrequency &&
      JSON.parse(this.esamConfigData[1].value) === this.esamAttributes.isPMRetrievalChecked &&
      JSON.parse(this.esamConfigData[3].value) ===
        this.esamAttributes.isControllerSwitchoverChecked &&
      this.esamConfigData[4].value === this.esamAttributes.switchoverDelay &&
      JSON.parse(this.esamConfigData[5].value) === this.esamAttributes.enableJmepRelease &&
      this.toHoursAndMinutes(this.esamConfigData[6].value) === this.esamAttributes.jmepReleaseDelay
    ) {
      this.esamAttributes.isEsamAttributesChanged = false;
    } else {
      this.esamAttributes.isEsamAttributesChanged = true;
    }
  }
  //eslint-disable-next-line
  toHoursAndMinutes(totalMinutes): string {
    this.jmepReleaseDelayHours = Math.floor(totalMinutes / 60);
    this.jmepReleaseDelayMinutes = totalMinutes % 60;
    if (this.jmepReleaseDelayHours < 10) {
      this.jmepReleaseDelayHours = '0' + this.jmepReleaseDelayHours;
    }
    if (this.jmepReleaseDelayMinutes < 10) {
      this.jmepReleaseDelayMinutes = '0' + this.jmepReleaseDelayMinutes;
    }
    return this.jmepReleaseDelayHours + ':' + this.jmepReleaseDelayMinutes;
  }
  //eslint-disable-next-line
  isNewConfigsSupported(esamVersion: string): any {
    let versionSupported = false;
    if (
      parseFloat(esamVersion) >= parseFloat(this.esamAttributes.esamNewConfigsSupportMinVersion)
    ) {
      versionSupported = true;
    }
    return versionSupported;
  }
  //eslint-disable-next-line
  onChangeJmepReleaseDelay(delayTime: string): any {
    let isValidTime = true;
    const validFormat = delayTime.match('[0-9][0-9]:[0-9][0-9]$') ? true : false;
    if (validFormat == true) {
      const hm = delayTime.split(':');
      const hours = parseInt(hm[0], 10);
      const minutes = parseInt(hm[1], 10);
      const duration = Number(hours) * 60 + Number(minutes);
      if (duration > this.esamAttributes.jmepReleaseMaxHours * 60) {
        isValidTime = false;
        this.invalidTimeMsg = 'Invalid time: The maximum hour(s) is 24hrs';
      } else if (minutes > 59) {
        isValidTime = false;
        this.invalidTimeMsg = 'Invalid minutes: where MM is 0-59';
      } else if (duration < this.esamAttributes.jmepReleaseMinMinutes) {
        isValidTime = false;
        this.invalidTimeMsg = 'Invalid time: The minimum minutes is 5min';
      }

      if (isValidTime) {
        this.invalidTimeMsg = null;
        this.esamAttributes.jmepReleaseDelayInMinutes = duration;
      }
    } else {
      this.invalidTimeMsg =
        'Valid format is: HH:MM where HH is 0-' +
        this.esamAttributes.jmepReleaseMaxHours +
        ', MM is 0-59';
    }
  }
  //eslint-disable-next-line
  updateDevice(payload: any): void {
    this.isLoading = true;
    this.dataSourceService
      .updateManagedDevice(payload)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        res => {
          if (this.esamAttributes.isEsamAttributesChanged) {
            this.dataSourceService
              .updateEsamConfig(this.esamAttributes.esamURL, this.createEsamPayload())
              .pipe(takeUntil(this.unsubscribe))
              .subscribe(
                () => {
                  this.isLoading = false;
                  this.deviceService.updateCache();
                  this.notificationService.onSuccess('Device has been updated');
                  this.dialogRef.close();
                },
                () => {
                  this.isLoading = false;
                  this.notificationService.onFail('Device not updated');
                  this.dialogRef.close();
                }
              );
          } else {
            this.isLoading = false;
            this.deviceService.updateCache();
            this.notificationService.onSuccess(res.message);
            this.dialogRef.close();
          }
        },
        err => {
          this.isLoading = false;
          this.notificationService.onFail(err.error.message);
          this.dialogRef.close();
        }
      );
  }
}
